import { useState, useEffect } from "react";
import DashboardLayout from "layouts/DashboardLayout";
import DashboardNavbar from "navbars/DashboardNavbar";
import { Grid, Skeleton } from "@mui/material";
import MDBox from "components/MDBox";
import PieChart from "widgets/Charts/PieChart";
import ComplexStatisticsCard from "widgets/Cards/StatisticsCards/ComplexStatisticsCard";
import VerticalBarChart from "widgets/Charts/BarCharts/VerticalBarChart";
import dashboardService from "../../services/dashboardService";
import { useNotification } from "../../context/NotificationContext";
import ListCard from "widgets/Cards/StatisticsCards/ListCard";

const Dashboard = () => {
    const { showNotification } = useNotification();
    const [loading, setLoading] = useState(true);
    const [filesCount, setFilesCount] = useState([]);
    const [createdStats, setCreatedStats] = useState([]);
    const [finishedStats, setFinishedStats] = useState([]);
    const [inactiveFileList, setInactiveFileList] = useState([]);
    const [inactiveFileSentList, setInactiveFileSentList] = useState([]);
    const [createdMonthly, setCreatedMonthly] = useState({ labels: [], datasets: [] });
    const [feeStats, setFeeStats] = useState([]);

    useEffect(() => {
        const fetchReport = async () => {
            try {
                const [
                    filesCountData,
                    createdStatsData,
                    finishedStatsData,
                    inactiveListData,
                    inactiveSentListData,
                    createdMonthlyData,
                    feeStatsData,
                ] = await Promise.all([
                    dashboardService.getExpedientesCount("2020-01-01", Date.now()),
                    dashboardService.getFilesCreatedStats(),
                    dashboardService.getFilesFinishedStats(),
                    dashboardService.getTop5InactiveFiles(),
                    dashboardService.getTop5InactiveFilesSent(),
                    dashboardService.getFilesCreatedMonthly(),
                    dashboardService.getFeeStats(),
                ]);

                if (
                    filesCountData.error ||
                    createdStatsData.error ||
                    finishedStatsData.error ||
                    inactiveListData.error ||
                    inactiveSentListData.error ||
                    createdMonthlyData.error ||
                    feeStatsData.error
                ) {
                    showNotification("Error al cargar el dashboard", "error");
                } else {
                    setFilesCount(filesCountData.data);
                    setCreatedStats(createdStatsData.data);
                    setFinishedStats(finishedStatsData.data);
                    setFeeStats(feeStatsData.data);

                    const inactiveFileListMap = Array.isArray(inactiveListData.data) && inactiveListData.data.length > 0
                        ? inactiveListData.data.map(item => ({
                            title: item.caratula,
                            subtitle: <span><b>[{item.estado}] {item.dias_inactivo} días</b></span>,
                            action: {
                                type: "internal",
                                route: `/file/detail/${item.id}`,
                                label: "Ver",
                                color: "info"
                            }
                        }))
                        : [];
                    setInactiveFileList(inactiveFileListMap);

                    const inactiveFileSentListMap = Array.isArray(inactiveSentListData.data) && inactiveSentListData.data.length > 0
                        ? inactiveSentListData.data.map(item => ({
                            title: item.caratula,
                            subtitle: <span><b>[{item.estado}] {item.dias_inactivo} días</b></span>,
                            action: {
                                type: "internal",
                                route: `/file/detail/${item.id}`,
                                label: "Ver",
                                color: "info"
                            }
                        }))
                        : [];
                    setInactiveFileSentList(inactiveFileSentListMap);

                    const createdMonthlyMap = Array.isArray(createdMonthlyData.data) && createdMonthlyData.data.length > 0
                        ? {
                            labels: createdMonthlyData.data.map(item => item.month),
                            datasets: [
                                {
                                    label: "Creados",
                                    color: "dark",
                                    data: createdMonthlyData.data.map(item => item.count),
                                },
                            ],
                        }
                        : { labels: [], datasets: [] };
                    setCreatedMonthly(createdMonthlyMap);
                }
            } catch (err) {
                showNotification(err.message, "error");
            } finally {
                setLoading(false);
            }
        };
        fetchReport();
    }, []);

    const chartData = Array.isArray(filesCount) && filesCount.length > 0
        ? {
            labels: filesCount.map(item => item.estado),
            datasets: [
                {
                    label: "Cantidad",
                    data: filesCount.map(item => item.cantidad),
                    backgroundColor: [
                        "#1976d2", "#2196f3", "#00bcd4", "#009688", "#43a047",
                        "#66bb6a", "#fbc02d", "#ffa000", "#fb8c00", "#e53935",
                        "#8e24aa", "#6d4c41", "#757575", "#607d8b", "#cfd8dc",
                        "#bdbdbd", "#f5f5f5", "#263238", "#388e3c", "#d32f2f"
                    ],
                    borderWidth: 2,
                    borderColor: "transparent",
                },
            ],
        }
        : {
            labels: [],
            datasets: [{ label: "Expedientes por Estado", data: [] }],
        };

    if (loading) {
        return (
            <DashboardLayout>
                <DashboardNavbar title="Dashboard" />
                <MDBox>
                    <Grid container spacing={3} mt={0}>
                        {[1, 2, 3].map((i) => (
                            <Grid item xs={12} md={4} key={i}>
                                <Skeleton variant="rectangular" height={130} sx={{ borderRadius: 2 }} />
                            </Grid>
                        ))}
                    </Grid>
                    <Grid container spacing={3} mt={3}>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rectangular" height={320} sx={{ borderRadius: 2 }} />
                        </Grid>
                    </Grid>
                    <Grid container spacing={3} mt={3}>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                        </Grid>
                        <Grid item xs={12} md={6}>
                            <Skeleton variant="rectangular" height={220} sx={{ borderRadius: 2 }} />
                        </Grid>
                    </Grid>
                </MDBox>
            </DashboardLayout>
        );
    }

    return (
        <DashboardLayout>
            <DashboardNavbar title="Dashboard" />
            <MDBox>
                <Grid container spacing={3} justifyContent="left" alignItems="left" height="100%" mt={0}>
                    <Grid item xs={12} md={4}>
                        <MDBox>
                            <ComplexStatisticsCard
                                icon="login"
                                title="Expedientes creados"
                                count={createdStats.count ?? 0}
                                percentage={{
                                    color: createdStats.variation < 0 ? "error" : "success",
                                    amount: `${createdStats.variation?.toFixed(2) ?? "0.00"} %`,
                                    label: "respecto al mes pasado"
                                }}
                            />
                        </MDBox>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MDBox>
                            <ComplexStatisticsCard
                                color="success"
                                icon="done_all"
                                title="Expedientes finalizados"
                                count={finishedStats.count ?? 0}
                                percentage={{
                                    color: finishedStats.variation < 0 ? "error" : "success",
                                    amount: `${finishedStats.variation?.toFixed(2) ?? "0.00"} %`,
                                    label: "respecto al mes pasado"
                                }}
                            />
                        </MDBox>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <MDBox>
                            <ComplexStatisticsCard
                                color="primary"
                                icon="attach_money"
                                title="Expedientes cobrados"
                                count={feeStats.count ?? 0}
                                percentage={{
                                    color: feeStats.variation < 0 ? "error" : "success",
                                    amount: `${feeStats.variation?.toFixed(2) ?? "0.00"} %`,
                                    label: "respecto al mes pasado"
                                }}
                            />
                        </MDBox>
                    </Grid>
                </Grid>

                <Grid container spacing={3} justifyContent="left" alignItems="left" height="100%" mt={3}>
                    <Grid item xs={12} md={6}>
                        <MDBox mb={3}>
                            <VerticalBarChart
                                icon={{ color: "info", component: "leaderboard" }}
                                color="info"
                                title="Expedientes creados"
                                description={<>Cantidad de expedientes creados por mes</>}
                                chart={createdMonthly}
                            />
                        </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MDBox>
                            <PieChart
                                title="Distribución"
                                description="Estado de los expedientes"
                                icon={{ color: "secondary", component: "leaderboard" }}
                                chart={chartData}
                            />
                        </MDBox>
                    </Grid>
                </Grid>

                <Grid container spacing={3} justifyContent="left" alignItems="left" height="100%" mt={3}>
                    <Grid item xs={12} md={6}>
                        <MDBox>
                            <ListCard
                                title="Top 5"
                                description="Expedientes inactivos"
                                icon={{ color: "warning", component: "disabled_visible" }}
                                list={inactiveFileList}
                            />
                        </MDBox>
                    </Grid>
                    <Grid item xs={12} md={6}>
                        <MDBox>
                            <ListCard
                                title="Top 5"
                                description="Expedientes enviados"
                                icon={{ color: "info", component: "send" }}
                                list={inactiveFileSentList}
                            />
                        </MDBox>
                    </Grid>
                </Grid>
            </MDBox>
        </DashboardLayout>
    );
};

export default Dashboard;
